import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Skeleton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button
} from '@mui/material';
import {
  Build,
  Schedule,
  Warning,
  CheckCircle,
  Assignment,
  EventAvailable,
  Flight
} from '@mui/icons-material';
import axios from 'axios';

const SummaryCard = ({ title, value, subtext, icon: Icon, color = 'primary' }) => (
  <Card sx={{ height: '100%', borderRadius: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {value}
          </Typography>
          {subtext != null && (
            <Typography variant="caption" color="text.secondary">
              {subtext}
            </Typography>
          )}
        </Box>
        {Icon && (
          <Box sx={{ bgcolor: `${color}.light`, color: `${color}.main`, p: 1.5, borderRadius: 2 }}>
            <Icon sx={{ fontSize: 28 }} />
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

const BarList = ({ items, labelKey, valueKey = 'count', emptyMessage }) => {
  const values = (items || []).map((i) => i[valueKey] || 0);
  const max = values.length ? Math.max(...values, 1) : 1;
  return (
    <List dense disablePadding>
      {!items?.length ? (
        <ListItem>
          <ListItemText primary={emptyMessage || 'No data'} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
        </ListItem>
      ) : (
        items.map((item, idx) => (
          <ListItem key={idx} disablePadding sx={{ py: 0.25 }}>
            <ListItemText
              primary={item[labelKey] ?? item._id ?? '—'}
              secondary={
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, ((item[valueKey] || 0) / max) * 100)}
                  sx={{ mt: 0.5, height: 6, borderRadius: 1, bgcolor: 'action.hover' }}
                  color="primary"
                />
              }
              primaryTypographyProps={{ variant: 'body2' }}
            />
            <Chip size="small" label={item[valueKey]} sx={{ ml: 1 }} />
          </ListItem>
        ))
      )}
    </List>
  );
};

/**
 * Maintenance & Inspections Dashboard – summary of work orders
 */
const MaintenanceDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get('/api/maintenance/summary');
        if (!cancelled) setSummary(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load summary');
          setSummary(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSummary();
    return () => { cancelled = true; };
  }, []);

  if (loading && !summary) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const data = summary || {};
  const byStatus = data.byStatus || [];
  const byPriority = data.byPriority || [];
  const byType = data.byType || [];
  const byAircraft = data.byAircraft || [];
  const exportDashboardCsv = () => {
    const rows = [
      { section: 'overview', metric: 'totalWorkOrders', value: data.total ?? 0 },
      { section: 'overview', metric: 'standaloneWO', value: data.standaloneWO ?? 0 },
      { section: 'overview', metric: 'upcoming30', value: data.upcoming30 ?? 0 },
      { section: 'overview', metric: 'upcoming90', value: data.upcoming90 ?? 0 },
      ...byStatus.map((item) => ({ section: 'byStatus', metric: item.status || '', value: item.count ?? 0 })),
      ...byPriority.map((item) => ({ section: 'byPriority', metric: item.priority || '', value: item.count ?? 0 })),
      ...byType.map((item) => ({ section: 'byType', metric: item.type || '', value: item.count ?? 0 })),
      ...byAircraft.map((item) => ({ section: 'byAircraft', metric: item.registration || '', value: item.count ?? 0 }))
    ];
    if (rows.length === 0) return;
    const headers = ['section', 'metric', 'value'];
    const csv = [headers.join(','), ...rows.map((row) => headers.map((key) => row[key]).join(','))].join('\n');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `maintenance-dashboard-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Overview
        </Typography>
        <Button variant="outlined" size="small" onClick={exportDashboardCsv}>
          Download CSV
        </Button>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total work orders"
            value={data.total ?? 0}
            icon={Build}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Standalone WO"
            value={data.standaloneWO ?? 0}
            subtext="Stand-alone emergency work orders"
            icon={Build}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Upcoming (30 days)"
            value={data.upcoming30 ?? 0}
            subtext="Scheduled in next 30 days"
            icon={Schedule}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Upcoming (90 days)"
            value={data.upcoming90 ?? 0}
            subtext="Scheduled in next 90 days"
            icon={EventAvailable}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Active (excl. completed/cancelled)"
            value={(data.total ?? 0) - (byStatus.find((s) => s.status === 'COMPLETED')?.count ?? 0) - (byStatus.find((s) => s.status === 'CANCELLED')?.count ?? 0)}
            icon={Assignment}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle fontSize="small" /> By status
              </Typography>
              <BarList items={byStatus} labelKey="status" emptyMessage="No work orders" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning fontSize="small" /> By priority
              </Typography>
              <BarList items={byPriority} labelKey="priority" emptyMessage="No work orders" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Build fontSize="small" /> By type
              </Typography>
              <BarList items={byType} labelKey="type" emptyMessage="No work orders" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Flight fontSize="small" /> By aircraft
              </Typography>
              <BarList items={byAircraft} labelKey="registration" emptyMessage="No work orders" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MaintenanceDashboard;
